import React, { useMemo, useState } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import Tooltip from '@mui/material/Tooltip';
import { downloadExcelSheetFunction } from '../../common/functions/download_excel_sheet..function';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  allSitesFiberSitePlanning,
  openAddInfoFormFiberSitePlanning,
  selectedFiberSitePlanningRow,
  fiberSitePlanningViewMode,
  openUploadFileRanDb,
} from '../../common/atom/atoms';
import edit from '../../Assets/AlertIcon/edits.svg';
import {
  IconButton,
  Menu,
  MenuItem,
  Button,
  Box,
  FormControl,
  Select,
  InputLabel,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
} from '@mui/material';
import { Chip } from '@mui/material';
import ReportAnalyticsIcon from '../../Assets/AlertIcon/report-analytics.svg';
import DownloadIconButton from '../../common/components/DownloadIconButton';
import GenerateReportFiberSitePlanning from './generate_report_fiber_site_planning.component';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FiberSiteDetailsModal from './FiberSiteDetailsModal';
import EditFiberSiteModal from './EditFiberSiteModal';
import EnhancedBulkEditModal from './EnhancedBulkEditModal';
import BulkDeleteWithColumnsModal from './components/BulkDeleteWithColumnsModal';
import ColumnSelectionModal from './components/ColumnSelectionModal';
import PaginationComponent from '../../common/components/PaginationComponent';
import { updateFiberSite } from './functions/update_fiber_site.function';
import { getAllFiberSitePlanningSites } from './functions/get_all_fiber_sites.function';
import { downloadExcelFiberSites } from './functions/download_excel_fiber_sites.function';
import { notify } from '../../common/functions/react-toastify.function';
import { useBackdrop } from '../../common/components/backDrop_context.component';
import FancyLoader from '../../common/components/FancyLoader.component';
import BulkUpdateProgressBar from './components/BulkUpdateProgressBar';
import { useFiberDashboard } from './context/fiberDashboardContext';
import * as XLSX from 'xlsx';
import { deleteFiberSite } from './functions/delete_fiber_site.function';
import { bulkDeleteFiberSites } from './functions/bulk_delete_fiber_sites.function';
import { convertDateToLocalString } from '../../common/functions/convert_date_to_local_string.function';

export default function FiberSitePlanningTable({
  currentPage = 1,
  totalPages = 1,
  totalCount = 0,
  pageSize = 50,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
  selectedStatus = 'All',
  onStatusChange,
  showPagination = true,
  searchQuery = '',
  onSearch,
  onClearSearch,
  isSearching = false,
  onSort,
  sortBy = '',
  sortOrder = 'ASC',
  columnFilters = [],
  onColumnFiltersChange,
}) {
  const data = useAtomValue(allSitesFiberSitePlanning);
  const { dashboardData, refreshDashboardData } = useFiberDashboard();
  const validData = Array?.isArray(data) ? data : [];
  const setOpenAddInfoForm = useSetAtom(openAddInfoFormFiberSitePlanning);
  const setOpenUploadFile = useSetAtom(openUploadFileRanDb);
  const setSelectedRow = useSetAtom(selectedFiberSitePlanningRow);
  const setAllSites = useSetAtom(allSitesFiberSitePlanning);
  const viewMode = useAtomValue(fiberSitePlanningViewMode);
  const { showBackdrop, hideBackdrop } = useBackdrop();
  const [openReportDialog, setOpenReportDialog] = useState(false);

  // Upload button state
  const [uploadAnchorEl, setUploadAnchorEl] = useState(null);
  const uploadMenuOpen = Boolean(uploadAnchorEl);

  // Download button state
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const downloadMenuOpen = Boolean(downloadAnchorEl);

  const handleUploadClick = (event) => {
    setUploadAnchorEl(event.currentTarget);
  };

  const handleUploadClose = () => {
    setUploadAnchorEl(null);
  };

  const handleAddSingleFile = () => {
    setOpenAddInfoForm(true);
    handleUploadClose();
  };

  const handleUploadFile = () => {
    setOpenUploadFile(true);
    handleUploadClose();
  };

  const handleDownloadClick = (event) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  const handleDownloadFTTSData = () => {
    downloadFile();
    handleDownloadClose();
  };

  const handleDownloadAll = async () => {
    try {
      setIsDownloading(true);
      await downloadExcelFiberSites();
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      notify('error', 'Failed to download Excel file. Please try again.');
    } finally {
      setIsDownloading(false);
      handleDownloadClose();
    }
  };

  const handleDownloadOnlyFTTS = () => {
    // Filter data to only include sites where is_ftts is "Yes"
    const fttsSites = dashboardData?.filter((site) => site.is_ftts === 'Yes') || [];

    if (fttsSites.length === 0) {
      notify('warning', 'No FTTS sites found to download.');
      handleDownloadClose();
      return;
    }

    downloadExcelSheetFunction(fttsSites, 'fiber');
    handleDownloadClose();
  };

  const handleDownloadColumnWise = () => {
    handleDownloadClose();
    setOpenColumnSelectionModal(true);
  };

  const handleDownloadSelectedColumns = (selectedColumnKeys) => {
    // Use all dashboard data (same as downloadFile function)
    const dataToDownload = dashboardData || [];

    if (dataToDownload.length === 0) {
      notify('warning', 'No data found to download.');
      return;
    }

    // Create a mapping of accessorKey to header for better column names in Excel
    const columnMap = {};
    columns.forEach((col) => {
      columnMap[col.accessorKey] = col.header;
    });

    // Prepare data with only selected columns
    const filteredData = dataToDownload.map((item) => {
      const filteredItem = {};
      selectedColumnKeys.forEach((key) => {
        filteredItem[columnMap[key] || key] = item[key] || '';
      });
      return filteredItem;
    });

    // Download using the existing function
    downloadExcelSheetFunction(filteredData, 'fiber');
    notify('success', `Downloaded ${selectedColumnKeys.length} columns successfully.`);
  };

  const [statusFilter, setStatusFilter] = useState('All');
  const [actionValue, setActionValue] = useState('');
  const [rowSelection, setRowSelection] = useState({});
  // Store selected site objects to preserve them across page changes
  const [selectedSitesMap, setSelectedSitesMap] = useState(new Map());
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openEnhancedEditModal, setOpenEnhancedEditModal] = useState(false);
  const [openBulkDeleteWithColumnsModal, setOpenBulkDeleteWithColumnsModal] = useState(false);
  const [deleteDialogState, setDeleteDialogState] = useState({
    open: false,
    mode: 'single',
    siteIds: [],
    siteLabels: [],
    isSubmitting: false,
  });

  // Progress tracking state
  const [bulkUpdateProgress, setBulkUpdateProgress] = useState({
    isOpen: false,
    totalSites: 0,
    completedSites: 0,
    successfulUpdates: [],
    failedUpdates: [],
    isProcessing: false,
    currentBatch: 0,
    totalBatches: 0,
  });

  // Search state
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const [localTableSearchQuery, setLocalTableSearchQuery] = useState('');
  const [isTableSearching, setIsTableSearching] = useState(false);
  const [tableSearchResults, setTableSearchResults] = useState(null);
  const handleColumnFiltersChangeInternal = React.useCallback(
    (updaterOrValue) => {
      if (typeof onColumnFiltersChange !== 'function') {
        return;
      }
      const nextFilters =
        typeof updaterOrValue === 'function' ? updaterOrValue(columnFilters) : updaterOrValue || [];
      onColumnFiltersChange(nextFilters);
    },
    [onColumnFiltersChange, columnFilters]
  );

  // Download state
  const [isDownloading, setIsDownloading] = useState(false);
  const [openColumnSelectionModal, setOpenColumnSelectionModal] = useState(false);

  // Universal sorting function that works for all columns
  const handleSortingChange = (updaterOrValue) => {
    const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue([]) : updaterOrValue;

    if (newSorting && newSorting.length > 0) {
      const sort = newSorting[0];
      const field = sort.id;
      const direction = sort.desc ? 'DESC' : 'ASC';

      if (onSort) {
        onSort(field, direction);
      }
    } else {
      // Clear sorting
      if (onSort) {
        onSort('', 'ASC');
      }
    }
  };

  // Handle search input changes
  const handleSearchChange = React.useCallback((event) => {
    const value = event.target.value;
    setLocalSearchQuery(value);
  }, []);

  // Handle table search input changes
  const handleTableSearchChange = React.useCallback((event) => {
    const value = event.target.value;
    setLocalTableSearchQuery(value);
  }, []);

  // Debounced search effect - only trigger when localSearchQuery changes (API search)
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (onSearch && localSearchQuery.trim().length > 1) {
        // Only search if site code has more than 1 character
        onSearch(localSearchQuery);
      } else if (onSearch && localSearchQuery.trim().length <= 1) {
        // Clear search if site code is 1 character or less
        if (isSearching) {
          onClearSearch();
        }
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [localSearchQuery, onSearch, onClearSearch, isSearching]);

  // Handle search clear
  const handleSearchClear = React.useCallback(() => {
    setLocalSearchQuery('');
    if (onClearSearch) {
      onClearSearch();
    }
  }, [onClearSearch]);

  // Handle table search clear
  const handleTableSearchClear = React.useCallback(() => {
    setLocalTableSearchQuery('');
    setIsTableSearching(false);
    setTableSearchResults(null);
  }, []);

  // Update local search query when prop changes
  React.useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  // Table search effect - searches dashboard data locally
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (localTableSearchQuery.trim().length > 1) {
        setIsTableSearching(true);
        // Search in dashboard data locally
        const searchData = dashboardData || [];
        const filteredResults = searchData.filter((site) => {
          const searchTerm = localTableSearchQuery.toLowerCase();
          return (
            (site.site_code && site.site_code.toLowerCase().includes(searchTerm)) ||
            (site.project_name && site.project_name.toLowerCase().includes(searchTerm)) ||
            (site.fiber_site_name && site.fiber_site_name.toLowerCase().includes(searchTerm)) ||
            (site.technical_city && site.technical_city.toLowerCase().includes(searchTerm)) ||
            (site.region && site.region.toLowerCase().includes(searchTerm)) ||
            (site.status && site.status.toLowerCase().includes(searchTerm))
          );
        });

        // Filter the results to only include the 30 specific fields for FTTS Site List
        const allowedFields = [
          'site_code',
          'project_name',
          'status',
          'onfp_planning_remarks',
          'bp_remarks',
          'comments',
          'phase_name',
          'bp',
          'fiber_site_name',
          'lat',
          'long',
          'technical_city',
          'region',
          'fiber_pop_id',
          'ring_name',
          'ring_unique_name',
          'ring_name_nms',
          'cutover_date',
          'total_length',
          'on_net',
          'off_net',
          'prefix',
          'old_site_code',
          'replacement_site_code',
          'replacement_site_name',
          'replacement_site_lat',
          'replacement_site_long',
          'replacement_site_proposed_ring',
          'addendum_status',
          'vendor',
          'descope_status',
          'is_ftts',
          'uploaded_at',
          'id',
        ];

        const filteredData = filteredResults.map((item) => {
          const filteredItem = {};
          allowedFields.forEach((field) => {
            if (item.hasOwnProperty(field)) {
              filteredItem[field] = item[field];
            }
          });
          return filteredItem;
        });

        setTableSearchResults(filteredData);
      } else if (localTableSearchQuery.trim().length <= 1) {
        // Clear search and return to original data
        setIsTableSearching(false);
        setTableSearchResults(null);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [localTableSearchQuery, dashboardData]);

  // Filter data based on status
  const filteredData = useMemo(() => {
    if (statusFilter === 'All') {
      return validData;
    }
    return validData.filter((item) => item.status === statusFilter);
  }, [validData, statusFilter]);

  // Use filtered data when table is searching, otherwise use the original filtered data
  const tableData = isTableSearching ? tableSearchResults || [] : filteredData;

  // Calculate status counts
  const statusCounts = useMemo(() => {
    const counts = {
      'On-Air': 0,
      'In-Progress': 0,
      Planned: 0,
      'Non-Fiber': 0,
      Descoped: 0,
      Total: tableData.length,
    };

    let fiberStatusCount = 0; // Count all sites with any status

    tableData.forEach((item) => {
      const status = item.status;
      if (counts.hasOwnProperty(status)) {
        counts[status]++;
      }
      // Count all sites that have any non-null status
      if (status && status.trim() !== '') {
        fiberStatusCount++;
      }
    });

    return {
      ...counts,
      FiberStatus: fiberStatusCount, // Add fiber status count
    };
  }, [tableData]);

  // Menu state for three-dot menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowForMenu, setSelectedRowForMenu] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedSiteForDetails, setSelectedSiteForDetails] = useState(null);

  const handleOpenMenu = (event, rowData) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowForMenu(rowData);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRowForMenu(null);
  };

  const handleViewSite = () => {
    setDetailsModalOpen(true);
    setSelectedSiteForDetails(selectedRowForMenu);
    handleCloseMenu();
  };

  const handleEditSite = () => {
    setSelectedRow(selectedRowForMenu);
    handleCloseMenu();
  };

  const getSiteLabel = (site) =>
    site?.site_code ||
    site?.fiber_site_name ||
    site?.project_name ||
    (site?.id ? `ID ${site.id}` : 'Unknown Site');

  // Update selected sites map when row selection changes on current page
  React.useEffect(() => {
    setSelectedSitesMap((prev) => {
      const newMap = new Map(prev);

      // Add/update current page's selected sites
      const currentPageSites = tableData.filter((row) => {
        const rowId = row.id?.toString();
        return rowId && rowSelection[rowId];
      });

      currentPageSites.forEach((site) => {
        const id = site.id?.toString();
        if (id) {
          newMap.set(id, site);
        }
      });

      // Remove sites that are no longer selected
      const selectedIds = Object.keys(rowSelection);
      selectedIds.forEach((id) => {
        if (!rowSelection[id]) {
          newMap.delete(id);
        }
      });

      return newMap;
    });
  }, [rowSelection, tableData]);

  // Helper function to get all selected sites from the full dataset (across all pages)
  const getSelectedSitesFromAllPages = useMemo(() => {
    const selectedSites = [];
    const selectedIds = Object.keys(rowSelection).filter((id) => rowSelection[id]);

    // First, try to get from stored selected sites map
    selectedIds.forEach((id) => {
      const storedSite = selectedSitesMap.get(id);
      if (storedSite) {
        selectedSites.push(storedSite);
      }
    });

    // For any missing sites, try to find them in dashboardData or validData
    const missingIds = selectedIds.filter((id) => !selectedSitesMap.has(id));
    if (missingIds.length > 0) {
      const allSitesData =
        Array.isArray(dashboardData) && dashboardData.length > 0 ? dashboardData : validData;

      missingIds.forEach((id) => {
        const site = allSitesData.find((row) => row.id?.toString() === id);
        if (site) {
          selectedSites.push(site);
        }
      });
    }

    return selectedSites;
  }, [rowSelection, dashboardData, validData, selectedSitesMap]);

  const canDeleteSite = (site) => {
    if (!site) {
      return false;
    }

    const hasStatus = site.status !== null && site.status !== undefined;

    return hasStatus;
  };

  const handleDeleteSite = () => {
    if (!selectedRowForMenu || !selectedRowForMenu.id) {
      notify('error', 'Unable to delete: invalid site data.');
      handleCloseMenu();
      return;
    }

    if (!canDeleteSite(selectedRowForMenu)) {
      notify('warning', 'Only sites with a valid status can be deleted.');
      handleCloseMenu();
      return;
    }

    setDeleteDialogState({
      open: true,
      mode: 'single',
      siteIds: [selectedRowForMenu.id],
      siteLabels: [getSiteLabel(selectedRowForMenu)],
      isSubmitting: false,
    });
    handleCloseMenu();
  };

  const handleCloseDeleteDialog = () => {
    if (deleteDialogState.isSubmitting) {
      return;
    }
    setDeleteDialogState({
      open: false,
      mode: 'single',
      siteIds: [],
      siteLabels: [],
      isSubmitting: false,
    });
  };

  const handleConfirmDelete = async () => {
    const { siteIds, mode } = deleteDialogState;

    if (!Array.isArray(siteIds) || siteIds.length === 0) {
      handleCloseDeleteDialog();
      return;
    }

    try {
      setDeleteDialogState((prev) => ({ ...prev, isSubmitting: true }));
      showBackdrop();

      if (mode === 'single') {
        await deleteFiberSite(siteIds[0]);
      } else {
        await bulkDeleteFiberSites(siteIds);
      }

      const remainingSites = validData.filter((site) => !siteIds.includes(site.id));
      setAllSites(remainingSites);
      setRowSelection({});

      try {
        await refreshDashboardData();
      } catch (refreshError) {
        console.error('Error refreshing dashboard data after delete:', refreshError);
      }

      notify(
        'success',
        mode === 'single'
          ? 'Site deleted successfully.'
          : `${siteIds.length} site(s) deleted successfully.`
      );
    } catch (error) {
      console.error('Error deleting fiber site(s):', error);
    } finally {
      hideBackdrop();
      setDeleteDialogState({
        open: false,
        mode: 'single',
        siteIds: [],
        siteLabels: [],
        isSubmitting: false,
      });
    }
  };

  // Handler for bulk delete with columns
  const handleBulkDeleteWithColumns = async (siteIds, progressCallback) => {
    const BATCH_SIZE = 10; // Process 10 sites at a time
    const TOTAL_SITES = siteIds.length;
    const successful = [];
    const failed = [];

    try {
      // Process in batches
      for (let i = 0; i < siteIds.length; i += BATCH_SIZE) {
        const batch = siteIds.slice(i, i + BATCH_SIZE);
        const batchNumber = Math.floor(i / BATCH_SIZE) + 1;
        const totalBatches = Math.ceil(TOTAL_SITES / BATCH_SIZE);

        try {
          // Delete batch
          await bulkDeleteFiberSites(batch);

          // Track successful deletions
          batch.forEach((id) => successful.push(id));

          // Update progress
          if (progressCallback) {
            progressCallback({
              completed: Math.min(i + BATCH_SIZE, TOTAL_SITES),
              total: TOTAL_SITES,
              successful,
              failed,
            });
          }
        } catch (error) {
          console.error(`Error deleting batch ${batchNumber}:`, error);
          // Track failed deletions
          batch.forEach((id) => failed.push(id));

          // Update progress even on error
          if (progressCallback) {
            progressCallback({
              completed: Math.min(i + BATCH_SIZE, TOTAL_SITES),
              total: TOTAL_SITES,
              successful,
              failed,
            });
          }
        }

        // Small delay between batches
        if (i + BATCH_SIZE < siteIds.length) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      // Update local state
      const remainingSites = validData.filter((site) => !siteIds.includes(site.id));
      setAllSites(remainingSites);
      setRowSelection({});

      // Refresh dashboard data
      try {
        await refreshDashboardData();
      } catch (refreshError) {
        console.error('Error refreshing dashboard data after delete:', refreshError);
      }

      // Show summary notification
      const successCount = successful.length;
      const failCount = failed.length;

      if (successCount > 0 && failCount === 0) {
        notify('success', `Successfully deleted all ${successCount} site(s)!`);
      } else if (successCount > 0 && failCount > 0) {
        notify(
          'warning',
          `${successCount} successful, ${failCount} failed out of ${TOTAL_SITES} sites`
        );
      } else {
        notify('error', `Failed to delete all ${TOTAL_SITES} site(s). Please try again.`);
      }
    } catch (error) {
      console.error('Error in bulk delete with columns:', error);
      notify('error', 'Error deleting sites. Please try again.');
    }
  };

  // Excel Bulk Edit Handler
  const handleExcelBulkEdit = () => {
    // Create a file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.xlsx,.xls';
    fileInput.style.display = 'none';

    fileInput.onchange = async (event) => {
      const file = event.target.files[0];
      if (!file) {
        return;
      }

      try {
        await processExcelFile(file);
      } catch (error) {
        console.error('Error processing Excel file:', error);
        notify('error', 'Error processing Excel file. Please check the file format.');
      } finally {
        // Clean up the file input
        document.body.removeChild(fileInput);
      }
    };

    // Add to DOM and trigger click
    document.body.appendChild(fileInput);
    fileInput.click();
  };

  const processExcelFile = async (file) => {
    try {
      showBackdrop();

      // Read the Excel file
      const excelData = await readExcelFile(file);

      if (excelData.length === 0) {
        notify('error', 'Excel file is empty or invalid.');
        return;
      }

      // Process each row
      const updatedSites = [];
      const errors = [];

      for (const row of excelData) {
        const result = await processExcelRow(row);
        if (result.success) {
          updatedSites.push(result.site);
        } else {
          errors.push(result.error);
        }
      }

      // Hide backdrop before starting bulk update (progress bar will handle UI blocking)
      hideBackdrop();

      // Show errors if any
      if (errors.length > 0) {
        const errorMessage = `Errors found for ${errors.length} site(s):\n${errors.join('\n')}`;
        notify('error', errorMessage);
      }

      // Update sites if any valid ones found
      if (updatedSites.length > 0) {
        await handleBulkUpdateSites(updatedSites);
        // Success message will be handled by handleBulkUpdateSites
      } else {
        notify('error', 'No valid sites found in Excel file.');
      }
    } catch (error) {
      console.error('Error processing Excel:', error);
      notify('error', 'Error processing Excel file. Please try again.');
    } finally {
      // Ensure backdrop is hidden
      hideBackdrop();
    }
  };

  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });

          // Get the first worksheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          // Convert to JSON with raw values to preserve original format
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            raw: true, // Get raw values to preserve original format
          });

          if (jsonData.length < 2) {
            resolve([]);
            return;
          }

          // Convert array format to object format
          const headers = jsonData[0];
          const dataRows = jsonData.slice(1);

          const result = dataRows.map((row) => {
            const obj = {};
            headers.forEach((header, index) => {
              obj[header] = row[index] || '';
            });
            return obj;
          });

          resolve(result);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (e) => reject(e);
      reader.readAsArrayBuffer(file);
    });
  };

  const processExcelRow = async (row) => {
    try {
      // Find site code column (case-insensitive)
      const siteCodeKey = findSiteCodeColumn(row);
      if (!siteCodeKey) {
        return { success: false, error: 'Site code column not found in Excel file' };
      }

      const siteCode = row[siteCodeKey];
      if (!siteCode) {
        return { success: false, error: 'Site code is empty' };
      }

      // Find matching site in dashboard data
      const matchingSite = dashboardData.find(
        (site) =>
          site.site_code === siteCode ||
          site.site_id === siteCode ||
          site.Site_Code === siteCode ||
          site.Site_Code === siteCode ||
          site['Site Code'] === siteCode
      );

      if (!matchingSite) {
        return { success: false, error: `Site code '${siteCode}' not found in current data` };
      }

      // Map Excel columns to fiber site fields
      const updatedFields = mapExcelColumnsToFiberFields(row);

      // Create updated site object
      const updatedSite = { ...matchingSite };
      Object.keys(updatedFields).forEach((key) => {
        if (
          updatedFields[key] !== undefined &&
          updatedFields[key] !== null &&
          updatedFields[key] !== ''
        ) {
          updatedSite[key] = updatedFields[key];
        }
      });

      return { success: true, site: updatedSite };
    } catch (error) {
      return { success: false, error: `Error processing row: ${error.message}` };
    }
  };

  const findSiteCodeColumn = (row) => {
    const possibleKeys = [
      'site_code',
      'Site_Code',
      'Site Code',
      'site_id',
      'Site_ID',
      'Site ID',
      'sitecode',
      'SiteCode',
      'SITECODE',
      'siteid',
      'SiteId',
      'SITEID',
    ];

    for (const key of possibleKeys) {
      if (row.hasOwnProperty(key)) {
        return key;
      }
    }

    // Also check case-insensitive match
    const rowKeys = Object.keys(row);
    for (const possibleKey of possibleKeys) {
      const found = rowKeys.find((rowKey) => rowKey.toLowerCase() === possibleKey.toLowerCase());
      if (found) {
        return found;
      }
    }

    return null;
  };

  // Function to preserve exact Excel date format
  const preserveExcelDateFormat = (value) => {
    // If it's a string, return as-is (this should be the formatted date from Excel)
    if (typeof value === 'string') {
      return value;
    }

    // If it's a number (Excel serial number), we need to convert it
    // but preserve the DD/MM/YYYY format
    if (typeof value === 'number') {
      // Excel epoch: 1900-01-01, but Excel incorrectly treats 1900 as leap year
      const excelEpoch = new Date(1900, 0, 1);
      let daysToAdd = value - 1;

      // Adjust for Excel's leap year bug
      if (value > 59) {
        daysToAdd = daysToAdd - 1;
      }

      const jsDate = new Date(excelEpoch.getTime() + daysToAdd * 24 * 60 * 60 * 1000);

      // Format as DD/MM/YYYY to match your Excel format
      const day = String(jsDate.getDate()).padStart(2, '0');
      const month = String(jsDate.getMonth() + 1).padStart(2, '0');
      const year = jsDate.getFullYear();

      return `${day}/${month}/${year}`;
    }

    return value;
  };

  const mapExcelColumnsToFiberFields = (row) => {
    const fieldMappings = {
      // Core fields
      status: ['status', 'Status', 'STATUS'],
      cutover_date: [
        'cutover_date',
        'Cutover_Date',
        'Cutover Date',
        'cutover',
        'Cutover',
        'CUTOVER',
      ],
      project_name: [
        'project_name',
        'Project_Name',
        'Project Name',
        'project',
        'Project',
        'PROJECT',
      ],
      onfp_planning_remarks: [
        'onfp_planning_remarks',
        'ONFP_Planning_Remarks',
        'ONFP Planning Remarks',
        'onfp_remarks',
        'ONFP_Remarks',
        'ONFP Remarks',
      ],
      bp_remarks: ['bp_remarks', 'BP_Remarks', 'BP Remarks', 'bp', 'BP'],
      comments: [
        'comments',
        'Comments',
        'COMMENTS',
        'note',
        'Note',
        'NOTE',
        'notes',
        'Notes',
        'NOTES',
      ],
      phase_name: ['phase_name', 'Phase_Name', 'Phase Name', 'phase', 'Phase', 'PHASE'],
      bp: ['bp', 'BP'],
      fiber_site_name: [
        'fiber_site_name',
        'Fiber_Site_Name',
        'Fiber Site Name',
        'site_name',
        'Site_Name',
        'Site Name',
      ],
      lat: ['lat', 'Lat', 'LAT', 'latitude', 'Latitude', 'LATITUDE'],
      long: [
        'long',
        'Long',
        'LONG',
        'longitude',
        'Longitude',
        'LONGITUDE',
        'lng',
        'Lng',
        'LNG',
        'lon',
        'Lon',
        'LON',
      ],
      technical_city: [
        'technical_city',
        'Technical_City',
        'Technical City',
        'city',
        'City',
        'CITY',
      ],
      region: ['region', 'Region', 'REGION'],
      vendor: ['vendor', 'Vendor', 'VENDOR'],
      fiber_pop_id: ['fiber_pop_id', 'Fiber_POP_ID', 'Fiber POP ID', 'pop_id', 'POP_ID', 'POP ID'],
      ring_name: ['ring_name', 'Ring_Name', 'Ring Name', 'ring', 'Ring', 'RING'],
      ring_unique_name: [
        'ring_unique_name',
        'Ring_Unique_Name',
        'Ring Unique Name',
        'unique_ring',
        'Unique_Ring',
        'Unique Ring',
      ],
      ring_name_nms: [
        'ring_name_nms',
        'Ring_Name_NMS',
        'Ring Name NMS',
        'nms_ring',
        'NMS_Ring',
        'NMS Ring',
      ],
      total_length: ['total_length', 'Total_Length', 'Total Length', 'length', 'Length', 'LENGTH'],
      on_net: ['on_net', 'On_Net', 'On Net', 'onnet', 'OnNet', 'ONNET'],
      off_net: ['off_net', 'Off_Net', 'Off Net', 'offnet', 'OffNet', 'OFFNET'],
      prefix: ['prefix', 'Prefix', 'PREFIX'],
      old_site_code: [
        'old_site_code',
        'Old_Site_Code',
        'Old Site Code',
        'old_code',
        'Old_Code',
        'Old Code',
      ],
      replacement_site_code: [
        'replacement_site_code',
        'Replacement_Site_Code',
        'Replacement Site Code',
        'replacement_code',
        'Replacement_Code',
        'Replacement Code',
      ],
      replacement_site_name: [
        'replacement_site_name',
        'Replacement_Site_Name',
        'Replacement Site Name',
        'replacement_name',
        'Replacement_Name',
        'Replacement Name',
      ],
      replacement_site_lat: [
        'replacement_site_lat',
        'Replacement_Site_Lat',
        'Replacement Site Lat',
        'replacement_lat',
        'Replacement_Lat',
        'Replacement Lat',
      ],
      replacement_site_long: [
        'replacement_site_long',
        'Replacement_Site_Long',
        'Replacement Site Long',
        'replacement_long',
        'Replacement_Long',
        'Replacement Long',
      ],
      replacement_site_proposed_ring: [
        'replacement_site_proposed_ring',
        'Replacement_Site_Proposed_Ring',
        'Replacement Site Proposed Ring',
        'proposed_ring',
        'Proposed_Ring',
        'Proposed Ring',
      ],
      addendum_status: [
        'addendum_status',
        'Addendum_Status',
        'Addendum Status',
        'addendum',
        'Addendum',
        'ADDENDUM',
      ],
      descope_status: [
        'descope_status',
        'Descope_Status',
        'Descope Status',
        'descope',
        'Descope',
        'DESCOPE',
      ],
      is_ftts: ['is_ftts', 'Is_FTTS', 'Is FTTS', 'IS_FTTS', 'isFtts', 'IsFtts', 'ftts', 'FTTS'],
    };

    const mappedFields = {};

    // Define date fields that need exact format preservation
    const dateFields = ['cutover_date', 'installation_date', 'commissioning_date'];

    Object.keys(fieldMappings).forEach((fiberField) => {
      const possibleKeys = fieldMappings[fiberField];

      for (const possibleKey of possibleKeys) {
        if (row.hasOwnProperty(possibleKey) && row[possibleKey]) {
          let value = row[possibleKey];

          // Preserve exact Excel format for date fields
          if (dateFields.includes(fiberField)) {
            value = preserveExcelDateFormat(value);
          }

          mappedFields[fiberField] = value;
          break;
        }
      }

      // Case-insensitive fallback
      if (!mappedFields[fiberField]) {
        const rowKeys = Object.keys(row);
        const found = rowKeys.find((rowKey) =>
          possibleKeys.some((possibleKey) => rowKey.toLowerCase() === possibleKey.toLowerCase())
        );
        if (found && row[found]) {
          let value = row[found];

          // Preserve exact Excel format for date fields
          if (dateFields.includes(fiberField)) {
            value = preserveExcelDateFormat(value);
          }

          mappedFields[fiberField] = value;
        }
      }
    });

    return mappedFields;
  };

  const handleActionChange = (action) => {
    if (action === 'edit') {
      // Handle bulk edit action for selected rows
      console.log('Bulk Edit Sites action selected');
      const selectedRows = Object.keys(rowSelection);
      console.log('Selected rows:', selectedRows);
      if (selectedRows.length === 0) {
        notify('warning', 'Please select at least one site to edit');
        setActionValue('');
        return;
      }

      // Get the selected row data from all pages using IDs
      const selectedRowData = getSelectedSitesFromAllPages;
      console.log('Selected row data:', selectedRowData);

      // Open the enhanced bulk edit modal
      setOpenEnhancedEditModal(true);

      // Reset the action value
      setActionValue('');
    } else if (action === 'bulkDelete') {
      const selectedRowData = getSelectedSitesFromAllPages;
      if (selectedRowData.length === 0) {
        notify('warning', 'Please select at least one site to delete');
        setActionValue('');
        return;
      }

      const deletableSites = selectedRowData.filter((site) => site?.id && canDeleteSite(site));
      if (deletableSites.length === 0) {
        notify('warning', 'Only sites with a valid status can be deleted.');
        setActionValue('');
        return;
      }

      if (deletableSites.length !== selectedRowData.length) {
        notify('warning', 'Some selected sites cannot be deleted because they have no status.');
        setActionValue('');
        return;
      }

      setDeleteDialogState({
        open: true,
        mode: 'bulk',
        siteIds: deletableSites.map((site) => site.id),
        siteLabels: deletableSites.map((site) => getSiteLabel(site)),
        isSubmitting: false,
      });
      setActionValue('');
    } else if (action === 'bulkDeleteWithColumns') {
      // Open bulk delete with columns modal
      setOpenBulkDeleteWithColumnsModal(true);
      setActionValue('');
    }
  };

  const columns = useMemo(() => {
    // Define only the 30 specific fields for FTTS Site List
    const allColumns = [
      // Site Code column with custom styling
      {
        accessorKey: 'site_code',
        header: 'Site Code',
        size: 120,
        muiTableHeadCellProps: {
          style: { paddingLeft: '16px' },
        },
        Cell: ({ cell }) => <div style={{ paddingLeft: '16px' }}>{cell.getValue()}</div>,
      },

      // Project Name
      { accessorKey: 'project_name', header: 'Project Name', size: 140 },

      // ONFP Planning Remarks
      {
        accessorKey: 'onfp_planning_remarks',
        header: 'ONFP Planning Remarks',
        size: 150,
      },

      // BP Remarks
      { accessorKey: 'bp_remarks', header: 'BP Remarks', size: 150 },

      // Comments
      { accessorKey: 'comments', header: 'Comments', size: 150 },

      // Phase Name
      { accessorKey: 'phase_name', header: 'Phase Name', size: 120 },

      // BP
      { accessorKey: 'bp', header: 'BP', size: 120 },

      // Fiber Site Name
      { accessorKey: 'fiber_site_name', header: 'Fiber Site Name', size: 140 },

      // Latitude
      { accessorKey: 'lat', header: 'Latitude', size: 120 },

      // Longitude
      { accessorKey: 'long', header: 'Longitude', size: 120 },

      // Technical City
      { accessorKey: 'technical_city', header: 'Technical City', size: 120 },

      // Region
      { accessorKey: 'region', header: 'Region', size: 120 },

      // Vendor
      { accessorKey: 'vendor', header: 'Vendor', size: 120 },

      // Fiber POP ID
      { accessorKey: 'fiber_pop_id', header: 'Fiber POP ID', size: 120 },

      // Ring Name
      { accessorKey: 'ring_name', header: 'Ring Name', size: 120 },

      // Ring Unique Name
      { accessorKey: 'ring_unique_name', header: 'Ring Unique Name', size: 140 },

      // Ring Name NMS
      { accessorKey: 'ring_name_nms', header: 'Ring Name NMS', size: 140 },

      // Cutover Date
      { accessorKey: 'cutover_date', header: 'Cutover Date', size: 120 },

      // Total Length
      { accessorKey: 'total_length', header: 'Total Length', size: 120 },

      // On Net
      { accessorKey: 'on_net', header: 'On Net', size: 120 },

      // Off Net
      { accessorKey: 'off_net', header: 'Off Net', size: 120 },

      // Prefix
      { accessorKey: 'prefix', header: 'Prefix', size: 120 },

      // Old Site Code
      { accessorKey: 'old_site_code', header: 'Old Site Code', size: 120 },

      // Replacement Site Code
      { accessorKey: 'replacement_site_code', header: 'Replacement Site Code', size: 140 },

      // Replacement Site Name
      { accessorKey: 'replacement_site_name', header: 'Replacement Site Name', size: 140 },

      // Replacement Site Latitude
      { accessorKey: 'replacement_site_lat', header: 'Replacement Site Latitude', size: 140 },

      // Replacement Site Longitude
      { accessorKey: 'replacement_site_long', header: 'Replacement Site Longitude', size: 140 },

      // Replacement Site Proposed Ring
      {
        accessorKey: 'replacement_site_proposed_ring',
        header: 'Replacement Site Proposed Ring',
        size: 160,
      },

      // Addendum Status
      { accessorKey: 'addendum_status', header: 'Addendum Status', size: 120 },

      // Descope Status
      { accessorKey: 'descope_status', header: 'Descope Status', size: 120 },

      // Is FTTS
      { accessorKey: 'is_ftts', header: 'Is FTTS', size: 100 },

      // Uploaded At
      {
        accessorKey: 'uploaded_at',
        header: 'Uploaded At',
        size: 160,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value ? convertDateToLocalString(value) : '--';
        },
      },

      // Status column with custom cell
      {
        accessorKey: 'status',
        header: 'Status',
        size: 120,
        Cell: ({ row }) => {
          const status = row.original.status;
          const getStatusColor = (status) => {
            switch (status) {
              case 'On-Air':
                return { label: 'On Air', color: '#12A150', bg: '#E8FAF0' };
              case 'In-Progress':
                return { label: 'In Progress', color: '#2196F3', bg: '#E5F6FD' };
              case 'Planned':
                return { label: 'Planned', color: '#BD1D23', bg: '#F8E8E9' };
              case 'Non-Fiber':
                return { label: 'Non Fiber', color: '#FF9800', bg: '#FFF3E0' };
              case 'Descoped':
                return { label: 'Descoped', color: '#9C27B0', bg: '#F3E5F5' };
              default:
                return { label: status, color: '#9E9E9E', bg: 'rgba(158, 158, 158, 0.1)' };
            }
          };
          const { label, color, bg } = getStatusColor(status);
          return status ? (
            <Chip
              label={label}
              size="small"
              style={{ color, backgroundColor: bg, fontWeight: 500, borderRadius: -1 }}
            />
          ) : null;
        },
      },
    ];

    return allColumns;
  }, []);

  // Define which columns should be visible by default - only essential columns
  const defaultColumnVisibility = {
    site_code: true,
    project_name: true,
    // All other columns are hidden by default
    onfp_planning_remarks: false,
    bp_remarks: false,
    comments: false,
    phase_name: true,
    bp: false,
    fiber_site_name: true,
    lat: false,
    long: false,
    technical_city: true,
    region: true,
    fiber_pop_id: false,
    ring_name: false,
    ring_unique_name: false,
    ring_name_nms: false,
    cutover_date: false,
    total_length: false,
    on_net: false,
    off_net: false,
    prefix: false,
    old_site_code: false,
    replacement_site_code: false,
    replacement_site_name: false,
    replacement_site_lat: false,
    replacement_site_long: false,
    replacement_site_proposed_ring: false,
    addendum_status: false,
    descope_status: false,
    is_ftts: true,
    uploaded_at: true,
    status: true,
  };

  const table = useMaterialReactTable({
    columns,
    data: tableData,
    getRowId: (row) => {
      // Use unique ID if available, otherwise fallback to site_code or generate unique ID
      if (row.id) {
        return row.id.toString();
      }
      if (row.site_code) {
        return `site_${row.site_code}`;
      }
      // Last resort: generate unique ID (should rarely happen)
      return `row_${Math.random().toString(36).substr(2, 9)}`;
    },
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
      sorting: sortBy ? [{ id: sortBy, desc: sortOrder === 'DESC' }] : [],
      columnFilters,
    },
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleColumnFiltersChangeInternal,
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableGrouping: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    enableColumnVisibility: true,
    initialState: {
      columnVisibility: defaultColumnVisibility,
      // density: 'compact',
    },
    enableDensityToggle: true,
    enableFullScreenToggle: false,
    enableHiding: true,
    enableSorting: true,
    enableFilters: true,
    enableGlobalFilter: false, // Disable built-in global filter
    enableColumnResizing: false,
    enableRowSelection: true,
    enableMultiRowSelection: true,
    enableSelectAll: true,
    enableRowActions: true,
    enableRowDragging: false,
    enableColumnDragging: false,
    enablePinning: true,
    enableRowVirtualization: false,
    enableColumnVirtualization: false,
    enablePagination: false,
    enableBottomToolbar: false,
    enableStickyFooter: false, // Remove empty footer since pagination is disabled
    enableStickyHeader: true, // Enable sticky header
    manualSorting: true, // Enable backend sorting
    manualFiltering: true,
    renderRowActions: ({ row }) => (
      <IconButton
        onClick={(e) => handleOpenMenu(e, row.original)}
        size="small"
        sx={{
          padding: '2px',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
    ),
    positionActionsColumn: 'last',
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '', // Hide the header text for actions column
      },
    },
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: 'calc(100vh - 275px)',
        overflowX: 'auto',
        width: '100%',
        '& .MuiTable-root': {
          width: '100%',
          tableLayout: 'auto', // Changed from 'fixed' to 'auto' for better action column positioning
        },
        '& .MuiTableCell-root': {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        '& .MuiTableRow-root .MuiTableCell-root:first-child': {
          width: '50px',
          minWidth: '50px',
          maxWidth: '50px',
          padding: '4px 8px',
          textAlign: 'left',
        },
        '& .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root:first-child': {
          width: '50px',
          minWidth: '50px',
          maxWidth: '50px',
          padding: '8px 8px',
          textAlign: 'left',
          backgroundColor: '#E8E9E9',
          color: '#344054',
          fontSize: '12px',
          fontWeight: '600',
          height: '32px',
        },
        '& .MuiTableRow-root .MuiTableCell-root:last-child': {
          width: '60px',
          minWidth: '60px',
          maxWidth: '60px',
          padding: '4px',
        },
        '& .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root:last-child': {
          width: '60px',
          minWidth: '60px',
          maxWidth: '60px',
          padding: '4px 8px',
          textAlign: 'center',
          backgroundColor: '#E8E9E9',
          color: '#344054',
          fontSize: '12px',
          fontWeight: '600',
          height: '32px',
        },
        // Ensure sticky header works properly
        '& .MuiTableHead-root': {
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: '#E8E9E9',
        },
        // Custom checkbox styling
        '& .MuiCheckbox-root': {
          padding: '2px',
          '& .MuiSvgIcon-root': {
            fontSize: '18px',
          },
        },
        '& .MuiCheckbox-root.Mui-checked': {
          color: '#BD1D23',
          '& .MuiSvgIcon-root': {
            fontSize: '18px',
          },
        },
        '& .MuiCheckbox-root.MuiCheckbox-indeterminate': {
          color: '#BD1D23',
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        color: '#667085',
        backgroundColor: 'transparent', // Changed from '#fff' to 'transparent' to inherit row color
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '125%',
        height: '20px', // Further reduced row height for compact density
        padding: '1px 4px', // Further reduced padding for compact density
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: (() => {
          const status = row.original.status; // This is the Status column
          if (status === 'On-Air') {
            return '#f0fff0'; // Very light green
          } else if (status === 'Planned') {
            return '#fff8f0'; // Very light orange
          } else if (status === 'In-Progress') {
            return '#f0f8ff'; // Very light blue
          } else if (status === 'Non-Fiber') {
            return '#fff8e0'; // Very light orange for Non-Fiber
          } else if (status === 'Descoped') {
            return '#f8f0ff'; // Very light purple for Descoped
          }
          return '#fff'; // Default white
        })(),
        '&:hover': {
          backgroundColor: (() => {
            const status = row.original.status; // This is the Status column
            if (status === 'On-Air') {
              return '#e6ffe6'; // Slightly darker green on hover
            } else if (status === 'Planned') {
              return '#ffe6cc'; // Slightly darker orange on hover
            } else if (status === 'In-Progress') {
              return '#e6f3ff'; // Slightly darker blue on hover
            } else if (status === 'Non-Fiber') {
              return '#ffe6cc'; // Slightly darker orange on hover for Non-Fiber
            } else if (status === 'Descoped') {
              return '#f0e6ff'; // Slightly darker purple on hover for Descoped
            }
            return '#f5f5f5'; // Default hover color
          })(),
        },
      },
    }),
    muiTableHeadCellProps: {
      sx: {
        color: '#344054',
        backgroundColor: '#E8E9E9',
        fontSize: '12px', // Reduced font size
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '125%',
        height: '32px', // Reduced header height
        padding: '4px 8px', // Reduced header padding
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: 'none',
        width: '100%',
        overflow: 'hidden',
        '& .MuiTableContainer-root': {
          overflowX: 'auto',
          maxWidth: '100%',
        },
        '& .MuiTable-root': {
          width: '100%',
          tableLayout: 'auto', // Changed from 'fixed' to 'auto' for better action column positioning
        },
        '& .MuiTableRow-root .MuiTableCell-root:last-child': {
          width: '60px',
          minWidth: '60px',
          maxWidth: '60px',
          padding: '4px',
          textAlign: 'center',
        },
        '& .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root:last-child': {
          width: '60px',
          minWidth: '60px',
          maxWidth: '60px',
          padding: '4px 8px',
          textAlign: 'center',
          backgroundColor: '#E8E9E9',
          color: '#344054',
          fontSize: '12px',
          fontWeight: '600',
          height: '32px',
        },
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '50%',
          flexWrap: 'nowrap',
          minWidth: 0,
          gap: 8,
        }}
      >
        {/* Search Input - API Search */}
        <TextField
          size="small"
          placeholder="Search all..."
          value={localSearchQuery}
          onChange={handleSearchChange}
          variant="outlined"
          sx={{
            minWidth: 200,
            '& .MuiOutlinedInput-root': {
              height: '32px',
              fontSize: '12px',
              '& fieldset': {
                borderColor: '#E0E0E0',
              },
              '&:hover fieldset': {
                borderColor: '#BD1D23',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#BD1D23',
              },
            },
            '& .MuiInputBase-input': {
              padding: '6px 12px',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: '#666' }} />
              </InputAdornment>
            ),
            endAdornment: localSearchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleSearchClear} sx={{ padding: '2px' }}>
                  <ClearIcon sx={{ fontSize: 16, color: '#666' }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Table Search Input - Local Search */}
        <TextField
          size="small"
          placeholder="Search in table..."
          value={localTableSearchQuery}
          onChange={handleTableSearchChange}
          variant="outlined"
          sx={{
            minWidth: 200,
            '& .MuiOutlinedInput-root': {
              height: '32px',
              fontSize: '12px',
              '& fieldset': {
                borderColor: '#E0E0E0',
              },
              '&:hover fieldset': {
                borderColor: '#BD1D23',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#BD1D23',
              },
            },
            '& .MuiInputBase-input': {
              padding: '6px 12px',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: '#666' }} />
              </InputAdornment>
            ),
            endAdornment: localTableSearchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleTableSearchClear} sx={{ padding: '2px' }}>
                  <ClearIcon sx={{ fontSize: 16, color: '#666' }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {/* Status Filter Dropdown */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel sx={{ fontSize: '12px', color: '#333' }}>Status</InputLabel>
          <Select
            value={selectedStatus}
            onChange={(e) => onStatusChange(e.target.value)}
            label="Status"
            sx={{
              fontSize: '12px',
              height: '32px',
              background: '#f8e8e9',
              boxShadow: '0 2px 8px rgba(168, 237, 234, 0.3)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
              },
              '& .MuiSelect-icon': {
                color: '#333',
              },
              '& .MuiSelect-select': {
                padding: '6px 12px',
                lineHeight: 1.2,
                color: '#333',
                fontWeight: 600,
              },
              '& .MuiInputLabel-root': {
                color: '#333',
                fontSize: '12px',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#333',
              },
              '& .MuiInputLabel-shrink': {
                color: '#333',
                fontSize: '11px',
              },
            }}
          >
            <MenuItem value="All" sx={{ fontSize: '12px' }}>
              All
            </MenuItem>
            <MenuItem value="On-Air" sx={{ fontSize: '12px' }}>
              On Air
            </MenuItem>
            <MenuItem value="In-Progress" sx={{ fontSize: '12px' }}>
              In Progress
            </MenuItem>
            <MenuItem value="Planned" sx={{ fontSize: '12px' }}>
              Planned
            </MenuItem>
            <MenuItem value="Non-Fiber" sx={{ fontSize: '12px' }}>
              Non Fiber
            </MenuItem>
            <MenuItem value="Descoped" sx={{ fontSize: '12px' }}>
              Descoped
            </MenuItem>
          </Select>
        </FormControl>

        {/* Upload Button */}
        <Button
          variant="contained"
          size="small"
          style={{
            background: '#BD1D23',
            color: 'white',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            padding: '6px 16px',
            fontSize: '12px',
            boxShadow: '0 2px 8px rgba(245, 248, 247, 0.3)',
            border: 'none',
            textTransform: 'none',
            height: '32px',
          }}
          endIcon={<ArrowDropDownIcon style={{ fontSize: 16, color: 'white' }} />}
          onClick={handleUploadClick}
        >
          Upload
        </Button>

        {/* Generate Report Button with Gradient */}
        <Button
          variant="contained"
          size="small"
          style={{
            background: '#f8e8e9',
            color: '#333',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            padding: '6px 16px',
            fontSize: '12px',
            boxShadow: '0 2px 8px rgba(168, 237, 234, 0.3)',
            border: 'none',
            textTransform: 'none',
          }}
          onClick={() => setOpenReportDialog(true)}
        >
          <img
            src={ReportAnalyticsIcon}
            alt="Report Analytics"
            style={{ width: 16, height: 16, marginRight: 4 }}
          />
          GENERATE REPORT
        </Button>

        {/* Download Button with Dropdown */}
        <Button
          variant="contained"
          size="small"
          style={{
            background: '#f8e8e9',
            color: '#333',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            padding: '6px 16px',
            fontSize: '12px',
            boxShadow: '0 2px 8px rgba(168, 237, 234, 0.3)',
            border: 'none',
            textTransform: 'none',
          }}
          onClick={handleDownloadClick}
          endIcon={<ArrowDropDownIcon style={{ fontSize: 16, color: '#333' }} />}
        >
          <DownloadIcon style={{ fontSize: 16, marginRight: 4, color: '#333' }} />
          Download
        </Button>

        {/* Bulk Edit with File Button */}
        <Button
          variant="contained"
          size="small"
          style={{
            background: '#f8e8e9',
            color: '#333',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            padding: '6px 16px',
            fontSize: '12px',
            boxShadow: '0 2px 8px rgba(168, 237, 234, 0.3)',
            border: 'none',
            textTransform: 'none',
            marginLeft: '8px',
          }}
          onClick={handleExcelBulkEdit}
        >
          <UploadFileIcon style={{ fontSize: 16, marginRight: 4, color: '#333' }} />
          Bulk Edit with Columns
        </Button>

        {/* Action Dropdown - Always show */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel sx={{ fontSize: '12px', color: '#333' }}>Action</InputLabel>
          <Select
            value={actionValue}
            onChange={(e) => {
              setActionValue(e.target.value);
              handleActionChange(e.target.value);
            }}
            label="Action"
            sx={{
              fontSize: '12px',
              height: '32px',
              background: '#f8e8e9',
              boxShadow: '0 2px 8px rgba(168, 237, 234, 0.3)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
              },
              '& .MuiSelect-icon': {
                color: '#333',
              },
              '& .MuiSelect-select': {
                padding: '6px 12px',
                lineHeight: 1.2,
                color: '#333',
                fontWeight: 600,
              },
              '& .MuiInputLabel-root': {
                color: '#333',
                fontSize: '12px',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#333',
              },
              '& .MuiInputLabel-shrink': {
                color: '#333',
                fontSize: '11px',
              },
            }}
          >
            <MenuItem value="edit" sx={{ fontSize: '12px' }}>
              <img src={edit} style={{ width: 16, height: 16, marginRight: 8 }} />
              Bulk Edit
            </MenuItem>
            <MenuItem value="bulkDelete" sx={{ fontSize: '12px' }}>
              <DeleteOutlineIcon sx={{ fontSize: 18, mr: 1 }} />
              Bulk Delete
            </MenuItem>
            <MenuItem value="bulkDeleteWithColumns" sx={{ fontSize: '12px' }}>
              <DeleteOutlineIcon sx={{ fontSize: 18, mr: 1 }} />
              Bulk Delete (with Columns)
            </MenuItem>
          </Select>
        </FormControl>

        {/* Selected Rows Counter */}
        {Object.keys(rowSelection).length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 0.5,
              borderRadius: '6px',
              backgroundColor: '#E3F2FD',
              border: '1px solid #2196F3',
              color: '#1976D2',
              fontSize: '12px',
              fontWeight: 600,
              height: '32px',
              minHeight: '32px',
              whiteSpace: 'nowrap',
            }}
          >
            {Object.keys(rowSelection).length} site(s) selected
          </Box>
        )}

        <div style={{ flex: 1, minWidth: 0 }} />
      </div>
    ),
  });

  const downloadFile = () => {
    // Use all dashboard data instead of filtered table data
    const dataToDownload = dashboardData || [];
    downloadExcelSheetFunction(dataToDownload, 'fiber');
  };

  const handleBulkUpdateSites = async (updatedSites) => {
    const BATCH_SIZE = 10; // Process 10 sites at a time
    const TOTAL_SITES = updatedSites.length;

    try {
      console.log(`Bulk updating ${TOTAL_SITES} sites in batches of ${BATCH_SIZE}`);

      // Initialize progress tracking
      const totalBatches = Math.ceil(TOTAL_SITES / BATCH_SIZE);
      setBulkUpdateProgress({
        isOpen: true,
        totalSites: TOTAL_SITES,
        completedSites: 0,
        successfulUpdates: [],
        failedUpdates: [],
        isProcessing: true,
        currentBatch: 0,
        totalBatches: totalBatches,
      });

      // Don't show backdrop for bulk updates - progress bar will handle the UI blocking

      // Initialize progress tracking
      let completedSites = 0;
      let successfulUpdates = [];
      let failedUpdates = [];

      // Show initial progress
      notify('info', `Starting bulk update of ${TOTAL_SITES} sites...`);

      // Process sites in batches
      for (let i = 0; i < updatedSites.length; i += BATCH_SIZE) {
        const batch = updatedSites.slice(i, i + BATCH_SIZE);
        const batchNumber = Math.floor(i / BATCH_SIZE) + 1;
        const totalBatches = Math.ceil(updatedSites.length / BATCH_SIZE);

        console.log(`Processing batch ${batchNumber}/${totalBatches} (${batch.length} sites)`);

        // Process current batch
        const batchPromises = batch.map(async (site) => {
          try {
            // Extract only the updated fields from the site
            const updatedFields = {};
            Object.keys(site).forEach((key) => {
              if (site[key] !== undefined && site[key] !== null) {
                updatedFields[key] = site[key];
              }
            });

            // Ensure off_net is always included in the payload if it exists in the site object
            // Include it even if it's null, undefined, or empty string
            if (site.hasOwnProperty('off_net')) {
              updatedFields.off_net =
                site.off_net !== undefined && site.off_net !== null ? site.off_net : '';
            }

            console.log(`Updating site ${site.site_code} with fields:`, updatedFields);

            // Call updateFiberSite API for each site
            const response = await updateFiberSite(site.id, updatedFields);
            console.log(`Successfully updated site ${site.site_code}:`, response);

            return { success: true, site: site.site_code, response };
          } catch (error) {
            console.error(`Error updating site ${site.site_code}:`, error);
            return { success: false, site: site.site_code, error: error.message };
          }
        });

        // Wait for current batch to complete
        const batchResults = await Promise.allSettled(batchPromises);

        // Process batch results
        batchResults.forEach((result) => {
          if (result.status === 'fulfilled') {
            if (result.value.success) {
              successfulUpdates.push(result.value);
            } else {
              failedUpdates.push(result.value);
            }
          } else {
            failedUpdates.push({ success: false, site: 'Unknown', error: result.reason });
          }
        });

        // Update progress
        completedSites += batch.length;
        const progress = Math.round((completedSites / TOTAL_SITES) * 100);

        // Update progress bar
        setBulkUpdateProgress((prev) => ({
          ...prev,
          completedSites,
          successfulUpdates,
          failedUpdates,
          currentBatch: batchNumber,
        }));

        // Show progress update for large batches
        if (TOTAL_SITES > 50) {
          console.log(`Progress: ${completedSites}/${TOTAL_SITES} (${progress}%)`);
          // Only show toast updates for significant progress milestones
          if (progress % 25 === 0 || completedSites === TOTAL_SITES) {
            notify(
              'info',
              `Progress: ${completedSites}/${TOTAL_SITES} sites processed (${progress}%)`
            );
          }
        }

        // Small delay between batches to prevent overwhelming the server
        if (i + BATCH_SIZE < updatedSites.length) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      console.log('Update results:', { successfulUpdates, failedUpdates });

      // Update progress bar with final results
      setBulkUpdateProgress((prev) => ({
        ...prev,
        isProcessing: false,
        completedSites: TOTAL_SITES,
        successfulUpdates,
        failedUpdates,
      }));

      // Show comprehensive summary using toast notifications
      const successCount = successfulUpdates.length;
      const failCount = failedUpdates.length;

      if (successCount > 0 && failCount === 0) {
        // All successful
        notify('success', `Successfully updated all ${successCount} site(s)!`);
      } else if (successCount > 0 && failCount > 0) {
        // Mixed results
        notify(
          'warning',
          `${successCount} successful, ${failCount} failed out of ${TOTAL_SITES} sites`
        );
      } else {
        // All failed
        notify(
          'error',
          `Failed to update all ${failCount} site(s). Please check the console for details.`
        );
      }

      // Update local state immediately to show changes without refresh
      if (successfulUpdates.length > 0) {
        try {
          // Create a map of updated sites for quick lookup
          const updatedSitesMap = {};
          successfulUpdates.forEach((update) => {
            const site = updatedSites.find((s) => s.site_code === update.site);
            if (site) {
              updatedSitesMap[site.site_code] = site;
            }
          });

          // Update the local data with the new values
          const updatedData = validData.map((site) => {
            if (updatedSitesMap[site.site_code]) {
              // Merge the updated fields with existing site data
              return { ...site, ...updatedSitesMap[site.site_code] };
            }
            return site;
          });

          // Update the atom state to reflect changes immediately
          setAllSites(updatedData);

          console.log('Local state updated with new values');

          // Refresh dashboard data to get updated information
          try {
            await refreshDashboardData();
            console.log('Dashboard data refreshed after bulk update');

            // Also refresh the detailed table data to update count
            // This will trigger a re-render with updated data
            window.dispatchEvent(new CustomEvent('refreshFiberTableData'));
          } catch (error) {
            console.error('Error refreshing dashboard data:', error);
            // Don't show error to user as the main operation was successful
          }
        } catch (stateUpdateError) {
          console.error('Error updating local state:', stateUpdateError);
        }
      }

      // Note: Local state is already updated above, so no need to refresh from API
      // This ensures immediate UI updates without waiting for server response

      // Close the modal
      setOpenEditModal(false);

      // Clear row selection
      setRowSelection({});
    } catch (error) {
      console.error('Error in bulk update process:', error);
      notify('error', 'Error updating sites. Please try again.');

      // Mark progress as failed
      setBulkUpdateProgress((prev) => ({
        ...prev,
        isProcessing: false,
      }));
    } finally {
      // No backdrop to hide for bulk updates
    }
  };

  // Handle progress bar close
  const handleProgressBarClose = () => {
    setBulkUpdateProgress((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };

  // Show subtle loader overlay when loading

  return (
    <div
      style={{
        marginLeft: '32px',
        marginRight: '32px',
        marginTop: '4px',
        width: 'calc(100% - 64px)',
      }}
    >
      <Box sx={{ width: '100%', overflow: 'hidden', position: 'relative' }}>
        <Box
          sx={{
            backgroundColor: '#fff',
            border: '1px solid #E0E0E0',
            borderRadius: '8px',
            marginBottom: '8px',
            boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden',
            width: '100%',
            position: 'relative',
          }}
        >
          {/* Loading overlay */}
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                // backgroundColor: 'rgba(255, 255, 255, 0.95)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                borderRadius: '8px',
                // boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                backdropFilter: 'blur(4px)',
              }}
            >
              <FancyLoader message="Loading..." fullScreen={false} size="small" />
            </Box>
          )}

          <div
            className="box"
            style={{
              width: '100%',
              overflow: 'hidden',
              padding: '0px',
              margin: '0px',
              marginBottom: '0px', // Remove bottom margin
            }}
          >
            <MaterialReactTable table={table} />
          </div>
        </Box>
      </Box>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
        <MenuItem onClick={handleViewSite}>
          <VisibilityIcon sx={{ mr: 1, fontSize: 20 }} />
          View
        </MenuItem>
        <MenuItem onClick={handleEditSite}>
          <img src={edit} style={{ width: 20, height: 20, marginRight: 8 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteSite}>
          <DeleteOutlineIcon sx={{ mr: 1, fontSize: 20 }} />
          Delete
        </MenuItem>
      </Menu>
      <Dialog
        open={deleteDialogState.open}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            {deleteDialogState.mode === 'single'
              ? 'Are you sure you want to delete this site?'
              : `Are you sure you want to delete the following ${deleteDialogState.siteIds.length} sites?`}
          </DialogContentText>
          {deleteDialogState.siteLabels.length > 0 && (
            <Box component="ul" sx={{ pl: 3, m: 0 }}>
              {deleteDialogState.siteLabels.map((label, index) => (
                <li key={`${label}-${index}`} style={{ marginBottom: '4px' }}>
                  {label}
                </li>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleCloseDeleteDialog}
            disabled={deleteDialogState.isSubmitting}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteDialogState.isSubmitting}
            startIcon={
              deleteDialogState.isSubmitting ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <FiberSiteDetailsModal
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        site={selectedSiteForDetails}
      />
      <GenerateReportFiberSitePlanning
        open={openReportDialog}
        onClose={() => setOpenReportDialog(false)}
      />

      {/* Bulk Edit Fiber Site Modal */}
      <EditFiberSiteModal
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        selectedSites={getSelectedSitesFromAllPages}
        onUpdate={handleBulkUpdateSites}
      />

      {/* Enhanced Bulk Edit Modal */}
      <EnhancedBulkEditModal
        open={openEnhancedEditModal}
        onClose={() => setOpenEnhancedEditModal(false)}
        selectedSites={getSelectedSitesFromAllPages}
        onUpdate={handleBulkUpdateSites}
        dashboardData={dashboardData}
      />

      {/* Bulk Delete With Columns Modal */}
      <BulkDeleteWithColumnsModal
        open={openBulkDeleteWithColumnsModal}
        onClose={() => setOpenBulkDeleteWithColumnsModal(false)}
        dashboardData={dashboardData}
        onConfirmDelete={handleBulkDeleteWithColumns}
      />

      {/* Bulk Update Progress Bar */}
      <BulkUpdateProgressBar
        open={bulkUpdateProgress.isOpen}
        onClose={handleProgressBarClose}
        totalSites={bulkUpdateProgress.totalSites}
        completedSites={bulkUpdateProgress.completedSites}
        successfulUpdates={bulkUpdateProgress.successfulUpdates}
        failedUpdates={bulkUpdateProgress.failedUpdates}
        isProcessing={bulkUpdateProgress.isProcessing}
        currentBatch={bulkUpdateProgress.currentBatch}
        totalBatches={bulkUpdateProgress.totalBatches}
      />

      {/* Total Records Display - Show when pagination is hidden */}
      {!showPagination && (
        <Box sx={{ mt: 2, mb: 1, display: 'flex', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {tableData.length} entries
          </Typography>
        </Box>
      )}

      {/* Pagination */}
      {showPagination && (
        <PaginationComponent
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          limit={pageSize}
          onPageChange={onPageChange}
          onLimitChange={onPageSizeChange}
        />
      )}

      {/* Upload Menu */}
      <Menu
        anchorEl={uploadAnchorEl}
        open={uploadMenuOpen}
        onClose={handleUploadClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleAddSingleFile}>Add Single Site</MenuItem>
        <MenuItem onClick={handleUploadFile}>Bulk Upload</MenuItem>
      </Menu>

      {/* Download Menu */}
      <Menu
        anchorEl={downloadAnchorEl}
        open={downloadMenuOpen}
        onClose={handleDownloadClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleDownloadFTTSData}>
          <DownloadIcon sx={{ mr: 1, fontSize: 20 }} />
          Download FTTS Data
        </MenuItem>
        <MenuItem onClick={handleDownloadAll} disabled={isDownloading}>
          {isDownloading ? (
            <CircularProgress size={20} sx={{ mr: 1 }} />
          ) : (
            <DownloadIcon sx={{ mr: 1, fontSize: 20 }} />
          )}
          {isDownloading ? 'Downloading...' : 'Download All'}
        </MenuItem>
        <MenuItem onClick={handleDownloadOnlyFTTS}>
          <DownloadIcon sx={{ mr: 1, fontSize: 20 }} />
          Download Only FTTS Sites
        </MenuItem>
        <MenuItem onClick={handleDownloadColumnWise}>
          <DownloadIcon sx={{ mr: 1, fontSize: 20 }} />
          Download Columns
        </MenuItem>
      </Menu>

      {/* Column Selection Modal */}
      <ColumnSelectionModal
        open={openColumnSelectionModal}
        onClose={() => setOpenColumnSelectionModal(false)}
        columns={columns}
        onDownload={handleDownloadSelectedColumns}
        data={dashboardData}
      />
    </div>
  );
}
